import { useEffect, useRef, useState } from "react";
import { createEventSource, type EventSourceClient } from "eventsource-client";
import { authTokenManager } from "@legal-500/auth-client";

type UseSSEStreamOptions<T> = {
  url: string;
  enabled?: boolean;
  onData: (data: T) => void;
  isDataEvent?: (data: unknown) => data is T;
  onError?: (error: Error) => void;
};

export function useSSEStream<T>({
  url,
  enabled = true,
  onData,
  isDataEvent,
  onError,
}: UseSSEStreamOptions<T>) {
  const [connected, setConnected] = useState(false);
  const onDataRef = useRef(onData);
  const isDataEventRef = useRef(isDataEvent);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onDataRef.current = onData;
  }, [onData]);

  useEffect(() => {
    isDataEventRef.current = isDataEvent;
  }, [isDataEvent]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }

    const headers: Record<string, string> = {};
    const token = authTokenManager.getToken();
    if (token) {
      headers["X-L500-Token"] = token;
    }

    let eventSource: EventSourceClient | null = null;
    try {
      eventSource = createEventSource({
        url,
        headers,
        onMessage: event => {
          setConnected(true);
          try {
            const parsed = JSON.parse(event.data) as unknown;
            const isValidEvent = isDataEventRef.current;
            if (isValidEvent && !isValidEvent(parsed)) return;
            const payload = parsed as T;
            onDataRef.current(payload);
          } catch (error) {
            onErrorRef.current?.(error instanceof Error ? error : new Error(String(error)));
          }
        },
        onDisconnect: () => {
          setConnected(false);
        },
      });
    } catch (error) {
      setConnected(false);
      onErrorRef.current?.(error instanceof Error ? error : new Error(String(error)));
    }

    return () => {
      setConnected(false);
      eventSource?.close();
    };
  }, [url, enabled]);

  return { connected };
}
